import { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, ClickAwayListener, IconButton, Paper, Popper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AddCircleOutline, FavoriteBorder } from '@mui/icons-material';
import type { CommunityReactionType } from '../../interfaces/ICommunityHub';
import { REACTION_TYPES } from '../../utils/reactionAssets';
import { reactionTokens } from '../../theme/tokens/reactions';
import { AnimatedReactionIcon } from './AnimatedReactionIcon';

interface PostReactionPickerProps {
    userReaction: CommunityReactionType | null;
    onSelect: (reaction: CommunityReactionType) => void;
    disabled?: boolean;
    message?: boolean;
    subject?: 'post' | 'comment' | 'message';
}

export function PostReactionPicker({ userReaction, onSelect, disabled = false, message = false, subject = message ? 'message' : 'post' }: PostReactionPickerProps) {
    const iconOnly = subject !== 'post';
    const theme = useTheme();
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const id = useId();
    const triggerRef = useRef<HTMLDivElement>(null);
    const firstChoiceRef = useRef<HTMLButtonElement>(null);
    const focusMenuRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressClickRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState<CommunityReactionType | null>(null);

    const clearTimer = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = null; };
    const close = () => { clearTimer(); setOpen(false); setPreview(null); };
    const scheduleClose = () => { clearTimer(); timerRef.current = setTimeout(close, reactionTokens.closeDelay); };
    const choose = (reaction: CommunityReactionType) => { close(); if (!disabled) onSelect(reaction); };
    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
    useEffect(() => {
        if (!open) return;
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
            setOpen(false);
            setPreview(null);
            triggerRef.current?.querySelector('button')?.focus();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open]);
    useEffect(() => {
        if (!open || !focusMenuRef.current) return;
        const frame = requestAnimationFrame(() => { firstChoiceRef.current?.focus(); focusMenuRef.current = false; });
        return () => cancelAnimationFrame(frame);
    }, [open]);

    return (
        <ClickAwayListener onClickAway={close}>
            <Box onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget) && !document.getElementById(id)?.contains(event.relatedTarget)) scheduleClose();
            }}>
                <Box ref={triggerRef} sx={{ display: 'inline-flex', alignItems: 'center' }}
                    onMouseEnter={() => { if (disabled) return; clearTimer(); timerRef.current = setTimeout(() => setOpen(true), reactionTokens.openDelay); }}
                    onMouseLeave={scheduleClose}>
                    <Button variant="text" disabled={disabled} onClick={() => {
                        if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                        if (iconOnly) { clearTimer(); setOpen(true); }
                        else choose(userReaction ?? 'Love');
                    }}
                        onPointerDown={event => {
                            suppressClickRef.current = false;
                            if (event.pointerType === 'mouse') return;
                            clearTimer();
                            timerRef.current = setTimeout(() => {
                                suppressClickRef.current = true;
                                setOpen(true);
                            }, reactionTokens.holdDelay);
                        }}
                        onPointerUp={clearTimer} onPointerCancel={close}
                        onContextMenu={event => event.preventDefault()}
                        onKeyDown={event => {
                            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                                event.preventDefault(); clearTimer(); focusMenuRef.current = true; setOpen(true);
                            }
                        }}
                        aria-haspopup="menu" aria-expanded={open && !disabled} aria-controls={open && !disabled ? id : undefined}
                        aria-label={iconOnly ? `React to ${subject}` : userReaction ? `Remove ${userReaction.toLowerCase()} reaction` : 'Like post'}
                        title={iconOnly ? `React to ${subject}` : undefined}
                        aria-pressed={iconOnly ? undefined : userReaction !== null}
                        sx={{ minHeight: reactionTokens.slot, gap: 1, color: userReaction ? 'primary.main' : 'text.secondary',
                            ...(iconOnly && { minWidth: reactionTokens.slot, width: reactionTokens.slot, p: 0, borderRadius: reactionTokens.circle }) }}>
                        {iconOnly ? <AddCircleOutline /> : userReaction ? <AnimatedReactionIcon reaction={userReaction} size={reactionTokens.selected} />
                            : <FavoriteBorder sx={{ width: reactionTokens.summary, height: reactionTokens.summary, color: 'text.secondary' }} />}
                        {!iconOnly && (userReaction ?? 'Like')}
                    </Button>
                </Box>
                <Popper open={open && !disabled} anchorEl={() => triggerRef.current!} placement="top-start"
                    modifiers={[{ name: 'offset', options: { offset: [0, reactionTokens.pickerGap] } },
                        { name: 'preventOverflow', options: { padding: reactionTokens.viewportMargin } }]}
                    sx={{ zIndex: theme.zIndex.tooltip }}>
                    <Paper id={id} role="menu" aria-label={`${subject === 'comment' ? 'Comment' : subject === 'message' ? 'Message' : 'Post'} reactions`} elevation={4}
                        onMouseEnter={clearTimer} onMouseLeave={scheduleClose}
                        onFocus={clearTimer}
                        onKeyDown={event => {
                            const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
                            const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
                            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                                event.preventDefault(); buttons[(index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
                            } else if (event.key === 'Home' || event.key === 'End') {
                                event.preventDefault(); buttons[event.key === 'Home' ? 0 : buttons.length - 1]?.focus();
                            }
                        }}
                        sx={{ display: 'flex', p: 1, borderRadius: reactionTokens.pill, overflow: 'visible', maxWidth: `calc(100vw - ${theme.spacing(4)})` }}>
                        {REACTION_TYPES.map((reaction, index) => (
                            <IconButton key={reaction} ref={index === 0 ? firstChoiceRef : undefined}
                                role="menuitem" aria-label={`React with ${reaction.toLowerCase()}`} aria-current={userReaction === reaction ? 'true' : undefined}
                                onClick={() => choose(reaction)}
                                onMouseEnter={() => setPreview(reaction)} onMouseLeave={() => setPreview(null)}
                                onFocus={() => setPreview(reaction)} onBlur={() => setPreview(null)}
                                sx={{ width: reactionTokens.slot, height: reactionTokens.slot, flex: '0 1 auto', p: 0, position: 'relative', zIndex: preview === reaction ? 1 : 0,
                                    borderRadius: reactionTokens.circle, bgcolor: userReaction === reaction ? 'action.selected' : 'transparent' }}>
                                <Box sx={{ display: 'flex', transformOrigin: index === 0 ? 'bottom left' : index === REACTION_TYPES.length - 1 ? 'bottom right' : 'bottom center',
                                    transform: !reduceMotion && preview === reaction ? `scale(${reactionTokens.preview / reactionTokens.artwork})` : 'none',
                                    transition: reduceMotion ? 'none' : theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }) }}>
                                    <AnimatedReactionIcon reaction={reaction} size={reactionTokens.artwork} animate loop />
                                </Box>
                                {preview === reaction && <Box sx={{ position: 'absolute', left: index === 0 ? 0 : undefined, right: index === REACTION_TYPES.length - 1 ? 0 : undefined,
                                    bottom: `calc(${(reactionTokens.slot - reactionTokens.artwork) / 2 + (reduceMotion ? reactionTokens.artwork : reactionTokens.preview)}px + ${theme.spacing(1)})`, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                                    <Typography component="span" variant="caption" sx={{ px: 1, py: 0.5, borderRadius: reactionTokens.pill, color: 'primary.contrastText', bgcolor: 'primary.main' }}>{reaction}</Typography>
                                </Box>}
                            </IconButton>
                        ))}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}